const BigNumber = require('bignumber.js');
const CardTypeRef = require('@/lib/PaymentMethods/CardTypeRef');
const PaymentChannel = require('@/lib/Channels/PaymentChannel');
const PaymentMethod = require('@/lib/PaymentMethods/PaymentMethod');
const PaymentMethodRef = require('@/lib/PaymentMethods/PaymentMethodRef');
const { ClubcardToken } = require('@/lib/Clubcard/ClubcardToken');
const { populate } = require('@/functions/objects');
const { toBool, toDate, toFloat, toNullableFloat, toNullableInt, toNullableString, toString } = require('@/functions/transform');

/**
 * @constructor
 * @param {object} [data]
 */
const Payment = function (data = {}) {
    /**
     * The total value of the payment.
     * You should not modify this directly. Instead call setTendered/setChangeGiven which will recalculate this.
     *
     * @type {number}
     */
    this.amount = 0.00;
    this.cashback = 0.00;
    this.changeGiven = 0.00;
    this.confirmedAt = null;
    this.gratuity = 0.00;
    this.ID = null;
    this.paid = true;
    this.pending = false;
    this.authCode = null;
    this.securityKey = null;
    this.status = null;

    /**
     * The complete raw response from the payment provider.
     * e.g. XML from Payment Express, or raw lines from WorldPay.
     *
     * The following payment methods used on ProPoint are known to store JSON here:
     * - card-mellon-webecr
     * - card-payyo-chip-and-pin
     * - tesco-voucher-uk
     * - tesco-voucher-ireland
     *
     * The following payment methods used on ProPoint are known to store non-JSON here:
     * - card-dojo-chip-and-pin
     * - card-verifone-chip-and-pin
     * - card-worldpay-chip-and-pin
     *
     * @type {string|null}
     */
    this.statusDetail = null;

    this.takenAt = new Date();
    this.tendered = 0;

    /**
     * The reference for this transaction at the payment provider.
     *
     * @type {string|null}
     */
    this.transactionRef = null;

    /**
     * @type {string|null}
     */
    this.thirdPartyID = null;

    /**
     * A token to refer back to the card/form of payment used.
     * Used if a new token was generated by this payment.
     *
     * @type {string|null}
     */
    this.token = null;

    /**
     * Used if an existing token was used for this payment.
     *
     * @type {number|null}
     */
    this.paymentTokenID = null;

    /**
     * For vouchers, this is the face value of the voucher. But as we don't give change for vouchers,
     * the amount/tendered may be less than this.
     *
     * @type {number|null}
     */
    this.value = null;

    /**
     * @type {PaymentMethod}
     */
    this.paymentMethod = null;

    /**
     * Last 4 digits of the card number / account number..
     *
     * @type {number|null}
     */
    this.lastDigits = null;

    /**
     * @type {PaymentChannel}
     */
    this.paymentChannel = null;

    /**
     * Remember what type of card was used, so it can be stored as the customerReference along with the last 4
     * digits of the card in a CustomerAccountPaymentToken.
     *
     * @type {string|null}
     */
    this.cardTypeRef = null;

    /**
     * Additional debugging info.
     * This will be sent to the API but is not stored with the payment. It will however be logged when the
     * payment is sent to the integrated payments log endpoint.
     *
     * @type {{}}
     */
    this.debugInfo = {};

    /**
     * This property is populated in 2 situations:
     *  1. When a payment is a (partial) refund of an existing online payment e.g. SagePay
     *  2. When we are creating a new (partial) refund payment against an existing online payment.
     *
     * Remember normally when we issue a refund in ProPoint we are actually making a new -ve payment not
     * reversing/refunding an existing payment.
     *
     * @type {number|null}
     */
    this.originalPaymentID = null;

    /**
     * If this is a refundable payment e.g. SagePay then this is maximum outstanding amount that can be refunded for
     * this payment.
     *
     * @type {float|null}
     */
    this.outstandingRefundableAmount = null;

    /**
     * @type {float|null}
     */
    this.pendingPartialRefundAmount = null;

    /**
     * A temporary identifier in format PAYMENT_METHOD_REF.int e.g. SAGE_PAY_SERVER.1
     *
     * @type {string|null}
     */
    this.paymentCompositeID = null;

    /**
     * @type {?ClubcardTokenWithItems}
     */
    this.clubcardTokenWithItems = null;

    populate(this, data);
};

Payment.prototype = {
    calculateAmount() {
        const tendered = new BigNumber(this.tendered.toFixed(8));
        const changeGiven = new BigNumber(this.changeGiven.toFixed(8));

        this.amount = tendered.minus(changeGiven).toNumber();
    },

    /**
     * @param {string|null} cardTypeRef
     */
    setCardTypeRef(cardTypeRef) {
        this.cardTypeRef = cardTypeRef;
    },

    /**
     * Return a human readable card type from the ref.
     *
     * @return {string}
     */
    getReadableCardTypeRef() {
        if (this.cardTypeRef) {
            switch (this.cardTypeRef) {
                case CardTypeRef.AMEX:
                    return 'Amex';
                case CardTypeRef.DINERS:
                    return 'Diners';
                case CardTypeRef.DISCOVER:
                    return 'Discover';
                case CardTypeRef.JCB:
                    return 'JCB';
                case CardTypeRef.LASER:
                    return 'Laser';
                case CardTypeRef.MAESTRO:
                    return 'Maestro';
                case CardTypeRef.MASTERCARD:
                    return 'Mastercard';
                case CardTypeRef.VISA:
                case CardTypeRef.VISA_CREDIT:
                case CardTypeRef.VISA_DEBIT:
                case CardTypeRef.VISA_ELECTRON:
                    return 'Visa';
            }
        }

        return '';
    },

    /**
     * @param {float|null} gratuity
     */
    setGratuity(gratuity) {
        this.gratuity = gratuity ? parseFloat(gratuity) : 0.00;
    },

    /**
     * @param {PaymentMethod} paymentMethod
     */
    setPaymentMethod(paymentMethod) {
        this.paymentMethod = paymentMethod;
    },

    /**
     * @param {string|null} thirdPartyID
     */
    setThirdPartyID(thirdPartyID) {
        this.thirdPartyID = thirdPartyID || null;
    },

    setStatus(status) {
        this.status = status;
    },

    setStatusDetail(statusDetail) {
        this.statusDetail = statusDetail;
    },

    setTransactionRef(transactionRef) {
        this.transactionRef = transactionRef;
    },

    setSecurityKey(securityKey) {
        this.securityKey = securityKey;
    },

    setAuthCode(authCode) {
        this.authCode = authCode;
    },

    setToken(token) {
        this.token = token;
    },

    setPaymentTokenID(paymentTokenID) {
        this.paymentTokenID = paymentTokenID;
    },

    setTendered(amountTendered) {
        this.tendered = parseFloat(amountTendered);
        this.calculateAmount();
    },

    setValue(value) {
        this.value = value;
    },

    setChangeGiven(changeGiven) {
        this.changeGiven = parseFloat(changeGiven);
        this.calculateAmount();
    },

    setLastDigits(lastDigits) {
        this.lastDigits = lastDigits;
    },

    /**
     * @param {number} cashback
     */
    setCashback(cashback) {
        this.cashback = cashback ? parseFloat(cashback) : 0.00;
    },

    setOriginalPaymentID(originalPaymentID) {
        this.originalPaymentID = originalPaymentID;
    },

    /**
     * Check if this is a failed payment (not paid and not pending).
     *
     * @return {boolean}
     */
    isFailed() {
        if (!this.paid && !this.pending) {
            // If the payment is Payment on Account not paid and not pending means 'Settled' rather than 'Failed'.
            if (this.paymentMethod && this.paymentMethod.ref === PaymentMethodRef.PAYMENT_ON_ACCOUNT) {
                return false;
            }

            return true;
        }

        return false;
    },

    /**
     * Settled is only used for POA payments. They are settled if they are not pending and not paid.
     *
     * @return {boolean}
     */
    isSettled() {
        if (this.paymentMethod && this.paymentMethod.ref === PaymentMethodRef.PAYMENT_ON_ACCOUNT) {
            return !this.paid && !this.pending;
        }

        return false;
    },

    /**
     * @return {boolean}
     */
    isClover() {
        return this.paymentMethod && this.paymentMethod.ref === PaymentMethodRef.CARD_CLOVER_CHIP_AND_PIN;
    },

    /**
     * @return {boolean}
     */
    isPayyo() {
        return this.paymentMethod && this.paymentMethod.ref === PaymentMethodRef.CARD_PAYYO_CHIP_AND_PIN;
    },

    /**
     * @return {boolean}
     */
    isVerifone() {
        return this.paymentMethod && this.paymentMethod.ref === PaymentMethodRef.CARD_VERIFONE_CHIP_AND_PIN;
    },

    getHydrationMap() {
        return {
            authCode: toNullableString,
            cashback: toFloat,
            cardTypeRef: toString,
            changeGiven: toFloat,
            clubcardToken: {
                model: ClubcardToken,
            },
            confirmedAt: toDate,
            gratuity: toFloat,
            ID: {
                field: ['ID', 'paymentID'],
                transform: toNullableInt,
            },
            lastDigits: {},
            originalPaymentID: toNullableInt,
            outstandingRefundableAmount: toNullableFloat,
            paid: toBool,
            paymentCompositeID: toNullableString,
            pending: toBool,
            pendingPartialRefundAmount: toNullableFloat,
            paymentChannel: {
                field: ['paymentChannel', 'paymentchannels'],
                model: PaymentChannel,
            },
            paymentMethod: {
                field: ['paymentMethod', 'paymentmethods'],
                model: PaymentMethod,
            },
            securityKey: toNullableString,
            status: toNullableString,
            statusDetail: toNullableString,
            takenAt: toDate,
            tendered: toFloat,
            thirdPartyID: toNullableString,
            transactionRef: toNullableString,
        };
    },

    afterHydration() {
        this.calculateAmount();
    },

    /**
     * Add a value to the status detail JSON string.
     * Note: not all payment methods store status detail as JSON!
     * Be careful to only use this method if you are sure the payment method stores status detail as JSON.
     *
     * @param {string} key
     * @param {any} value
     */
    addToJsonStatusDetail(key, value) {
        let statusDetail = this.getJsonStatusDetail();
        statusDetail[key] = value;
        this.statusDetail = JSON.stringify(statusDetail);
    },

    getJsonStatusDetail() {
        return this.statusDetail ? JSON.parse(this.statusDetail) : {};
    },

    getFromJsonStatusDetail(key) {
        const statusDetail = this.getJsonStatusDetail();
        return statusDetail[key] || null;
    },

    /**
     * @returns {boolean}
     */
    printAuthCode() {
        return !!(
            this.authCode
            && [
                PaymentMethodRef.CARD_MELLON_WEBECR,
            ].includes(this.paymentMethod.ref)
        );
    },
};

module.exports = Payment;
